import React, { useState } from 'react';
import { UserPlusIcon, UsersIcon, PlusIcon, MoreHorizontalIcon, SearchIcon, ArrowLeftIcon, ChevronRightIcon, MessageCircleIcon, CalendarIcon, TagIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BackLink } from '../components/BackLink';
// Types
type Circle = {
  id: string;
  name: string;
  members: Friend[];
  interests: string[];
};
type Friend = {
  id: string;
  name: string;
  avatar: string;
  status?: string;
};
export const FriendsAndCirclesPage = () => {
  // State
  const [activeTab, setActiveTab] = useState<'circles' | 'friends'>('circles');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCreateCircleModal, setShowCreateCircleModal] = useState(false);
  const [selectedCircle, setSelectedCircle] = useState<Circle | null>(null);
  const [circleActiveTab, setCircleActiveTab] = useState<'feed' | 'plans' | 'interests'>('feed');
  // Sample data
  const circles: Circle[] = [{
    id: '1',
    name: 'Game Day Crew',
    members: [{
      id: '1',
      name: 'Alex Johnson',
      avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=1287&auto=format&fit=crop'
    }, {
      id: '2',
      name: 'Morgan Lee',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1361&auto=format&fit=crop'
    }, {
      id: '3',
      name: 'Taylor Kim',
      avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?q=80&w=1480&auto=format&fit=crop'
    }, {
      id: '4',
      name: 'Jordan Smith',
      avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=1480&auto=format&fit=crop'
    }],
    interests: ['NBA', 'Lakers', 'Basketball']
  }, {
    id: '2',
    name: 'Concert Buddies',
    members: [{
      id: '2',
      name: 'Morgan Lee',
      avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1361&auto=format&fit=crop'
    }, {
      id: '5',
      name: 'Riley Parker',
      avatar: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?q=80&w=1470&auto=format&fit=crop'
    }, {
      id: '6',
      name: 'Casey Wilson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=1470&auto=format&fit=crop'
    }],
    interests: ['Music', 'Concerts', 'Taylor Swift', 'Live Shows']
  }, {
    id: '3',
    name: 'Foodie Friends',
    members: [{
      id: '1',
      name: 'Alex Johnson',
      avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=1287&auto=format&fit=crop'
    }, {
      id: '3',
      name: 'Taylor Kim',
      avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?q=80&w=1480&auto=format&fit=crop'
    }, {
      id: '6',
      name: 'Casey Wilson',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=1470&auto=format&fit=crop'
    }],
    interests: ['Restaurants', 'Food Festivals', 'Cooking']
  }];
  const friends: Friend[] = [{
    id: '1',
    name: 'Alex Johnson',
    avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?q=80&w=1287&auto=format&fit=crop',
    status: 'NBA fan'
  }, {
    id: '2',
    name: 'Morgan Lee',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1361&auto=format&fit=crop',
    status: 'Concert lover'
  }, {
    id: '3',
    name: 'Taylor Kim',
    avatar: 'https://images.unsplash.com/photo-1633332755192-727a05c4013d?q=80&w=1480&auto=format&fit=crop',
    status: 'Foodie enthusiast'
  }, {
    id: '4',
    name: 'Jordan Smith',
    avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?q=80&w=1480&auto=format&fit=crop',
    status: 'Sports fan'
  }, {
    id: '5',
    name: 'Riley Parker',
    avatar: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?q=80&w=1470&auto=format&fit=crop',
    status: 'Music lover'
  }, {
    id: '6',
    name: 'Casey Wilson',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=1470&auto=format&fit=crop',
    status: 'Food explorer'
  }];
  // Sample events for the feed
  const events = [{
    id: '1',
    title: 'Lakers vs Warriors',
    date: 'Sat, Nov 18 • 7:30 PM',
    location: 'Crypto.com Arena',
    image: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?q=80&w=2069&auto=format&fit=crop',
    interested: 3
  }, {
    id: '2',
    title: 'NBA All-Star Weekend',
    date: 'Feb 16-18, 2024',
    location: 'Indianapolis, IN',
    image: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?q=80&w=2069&auto=format&fit=crop',
    interested: 2
  }];
  // Sample plans
  const plans = [{
    id: '1',
    title: 'Watch party for Lakers vs Warriors',
    creator: 'Alex Johnson',
    date: 'Sat, Nov 18 • 7:00 PM',
    location: "Alex's place",
    responses: [{
      name: 'Morgan Lee',
      status: 'going'
    }, {
      name: 'Taylor Kim',
      status: 'maybe'
    }]
  }, {
    id: '2',
    title: 'NBA Finals Game 1 at Sports Bar',
    creator: 'Jordan Smith',
    date: 'Thu, Jun 6, 2024 • 6:00 PM',
    location: 'Overtime Sports Bar',
    responses: [{
      name: 'Alex Johnson',
      status: 'going'
    }, {
      name: 'Taylor Kim',
      status: 'going'
    }]
  }];
  // Handlers
  const handleOpenCircle = (circle: Circle) => {
    setSelectedCircle(circle);
    setCircleActiveTab('feed');
  };
  const handleBackToMain = () => {
    setSelectedCircle(null);
  };
  // Render main friends and circles hub
  const renderMainHub = () => <div className="p-6" data-id="element-557">
      <BackLink to="/app" className="mb-4" data-id="element-558">
        Back to Dashboard
      </BackLink>
      <div className="flex justify-between items-center mb-6" data-id="element-559">
        <h1 className="text-2xl font-bold text-[#F5F5F7]" data-id="element-560">Friends & Circles</h1>
        <button onClick={() => setShowInviteModal(true)} className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors flex items-center" data-id="element-561">
          <UserPlusIcon size={18} className="mr-2" data-id="element-562" />
          Invite Friend
        </button>
      </div>
      <div className="mb-6" data-id="element-563">
        <div className="flex border-b border-gray-700" data-id="element-564">
          <button className={`py-3 px-4 font-medium ${activeTab === 'circles' ? 'text-[#F5F5F7] border-b-2 border-[#007BFF]' : 'text-gray-400'}`} onClick={() => setActiveTab('circles')} data-id="element-565">
            Circles
          </button>
          <button className={`py-3 px-4 font-medium ${activeTab === 'friends' ? 'text-[#F5F5F7] border-b-2 border-[#007BFF]' : 'text-gray-400'}`} onClick={() => setActiveTab('friends')} data-id="element-566">
            All Friends
          </button>
        </div>
      </div>
      {activeTab === 'circles' ? <div data-id="element-567">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6" data-id="element-568">
            {circles.map(circle => <div key={circle.id} className="bg-[#2C2F33] rounded-xl p-4 hover:bg-[#3A3D42] transition-colors cursor-pointer" onClick={() => handleOpenCircle(circle)} data-id="element-569">
                <div className="flex justify-between items-start mb-3" data-id="element-570">
                  <h3 className="text-lg font-bold text-[#F5F5F7]" data-id="element-571">
                    {circle.name}
                  </h3>
                  <button className="text-gray-400 hover:text-[#F5F5F7]" data-id="element-572">
                    <MoreHorizontalIcon size={20} data-id="element-573" />
                  </button>
                </div>
                <div className="flex items-center mb-3" data-id="element-574">
                  <div className="flex -space-x-2" data-id="element-575">
                    {circle.members.slice(0, 3).map(member => <div key={member.id} className="w-8 h-8 rounded-full border-2 border-[#2C2F33] overflow-hidden" data-id="element-576">
                        <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" data-id="element-577" />
                      </div>)}
                    {circle.members.length > 3 && <div className="w-8 h-8 rounded-full bg-gray-700 border-2 border-[#2C2F33] flex items-center justify-center text-xs text-[#F5F5F7]" data-id="element-578">
                        +{circle.members.length - 3}
                      </div>}
                  </div>
                  <span className="ml-2 text-gray-400 text-sm" data-id="element-579">
                    {circle.members.length} members
                  </span>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-580">
                  {circle.interests.slice(0, 2).map(interest => <span key={interest} className="bg-[#1A1A1D] text-gray-300 text-xs px-2 py-1 rounded-full" data-id="element-581">
                      {interest}
                    </span>)}
                  {circle.interests.length > 2 && <span className="bg-[#1A1A1D] text-gray-300 text-xs px-2 py-1 rounded-full" data-id="element-582">
                      +{circle.interests.length - 2} more
                    </span>}
                </div>
              </div>)}
            <div className="bg-[#2C2F33] bg-opacity-50 rounded-xl p-4 border-2 border-dashed border-gray-600 flex flex-col items-center justify-center text-center hover:bg-opacity-70 transition-colors cursor-pointer h-[160px]" onClick={() => setShowCreateCircleModal(true)} data-id="element-583">
              <div className="w-12 h-12 rounded-full bg-[#1A1A1D] flex items-center justify-center mb-3" data-id="element-584">
                <PlusIcon size={24} className="text-[#007BFF]" data-id="element-585" />
              </div>
              <h3 className="text-lg font-medium text-[#F5F5F7] mb-1" data-id="element-586">
                Create New Circle
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-587">
                Group friends with shared interests
              </p>
            </div>
          </div>
        </div> : <div data-id="element-588">
          <div className="mb-4 relative" data-id="element-589">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-590" />
            <input type="text" placeholder="Search friends..." className="w-full bg-[#2C2F33] text-[#F5F5F7] rounded-lg pl-10 pr-4 py-2 border border-gray-700 focus:border-[#007BFF] focus:outline-none" data-id="element-591" />
          </div>
          <div className="space-y-2" data-id="element-592">
            {friends.map(friend => <div key={friend.id} className="bg-[#2C2F33] rounded-lg p-3 flex items-center justify-between" data-id="element-593">
                <div className="flex items-center" data-id="element-594">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-595">
                    <img src={friend.avatar} alt={friend.name} className="w-full h-full object-cover" data-id="element-596" />
                  </div>
                  <div data-id="element-597">
                    <h3 className="text-[#F5F5F7] font-medium" data-id="element-598">
                      {friend.name}
                    </h3>
                    {friend.status && <p className="text-gray-400 text-sm" data-id="element-599">{friend.status}</p>}
                  </div>
                </div>
                <button className="text-gray-400 hover:text-[#F5F5F7]" data-id="element-600">
                  <MoreHorizontalIcon size={20} data-id="element-601" />
                </button>
              </div>)}
          </div>
        </div>}
    </div>;
  // Render single circle view
  const renderCircleHub = () => {
    if (!selectedCircle) return null;
    return <div className="p-6" data-id="element-602">
        <button onClick={handleBackToMain} className="flex items-center text-gray-400 hover:text-[#F5F5F7] mb-4" data-id="element-603">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-604" />
          Back to Circles
        </button>
        <div className="flex justify-between items-center mb-6" data-id="element-605">
          <h1 className="text-2xl font-bold text-[#F5F5F7]" data-id="element-606">
            {selectedCircle.name}
          </h1>
          <button className="border border-gray-600 text-gray-300 px-3 py-1.5 rounded-lg hover:bg-[#2C2F33] transition-colors text-sm" data-id="element-607">
            Manage Circle
          </button>
        </div>
        <div className="mb-6 overflow-x-auto pb-2" data-id="element-608">
          <div className="flex space-x-2" data-id="element-609">
            {selectedCircle.members.map(member => <div key={member.id} className="flex flex-col items-center" data-id="element-610">
                <div className="w-12 h-12 rounded-full overflow-hidden mb-1" data-id="element-611">
                  <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" data-id="element-612" />
                </div>
                <span className="text-gray-300 text-xs whitespace-nowrap" data-id="element-613">
                  {member.name.split(' ')[0]}
                </span>
              </div>)}
            <div className="flex flex-col items-center" data-id="element-614">
              <div className="w-12 h-12 rounded-full bg-[#1A1A1D] border border-dashed border-gray-600 flex items-center justify-center mb-1" data-id="element-615">
                <PlusIcon size={18} className="text-gray-400" data-id="element-616" />
              </div>
              <span className="text-gray-400 text-xs whitespace-nowrap" data-id="element-617">
                Add
              </span>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-618">
          <div className="flex border-b border-gray-700" data-id="element-619">
            <button className={`py-3 px-4 font-medium ${circleActiveTab === 'feed' ? 'text-[#F5F5F7] border-b-2 border-[#007BFF]' : 'text-gray-400'}`} onClick={() => setCircleActiveTab('feed')} data-id="element-620">
              Event Feed
            </button>
            <button className={`py-3 px-4 font-medium ${circleActiveTab === 'plans' ? 'text-[#F5F5F7] border-b-2 border-[#007BFF]' : 'text-gray-400'}`} onClick={() => setCircleActiveTab('plans')} data-id="element-621">
              Plans & Chat
            </button>
            <button className={`py-3 px-4 font-medium ${circleActiveTab === 'interests' ? 'text-[#F5F5F7] border-b-2 border-[#007BFF]' : 'text-gray-400'}`} onClick={() => setCircleActiveTab('interests')} data-id="element-622">
              Shared Interests
            </button>
          </div>
        </div>
        {circleActiveTab === 'feed' && <div data-id="element-623">
            <div className="mb-4 flex justify-between items-center" data-id="element-624">
              <h2 className="text-xl font-semibold text-[#F5F5F7]" data-id="element-625">
                Upcoming Events
              </h2>
              <button className="text-[#007BFF] hover:underline text-sm" data-id="element-626">
                View All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-627">
              {events.map(event => <div key={event.id} className="bg-[#2C2F33] rounded-xl overflow-hidden" data-id="element-628">
                  <div className="h-32 relative" data-id="element-629">
                    <img src={event.image} alt={event.title} className="w-full h-full object-cover" data-id="element-630" />
                  </div>
                  <div className="p-4" data-id="element-631">
                    <h3 className="text-[#F5F5F7] font-bold text-lg mb-1" data-id="element-632">
                      {event.title}
                    </h3>
                    <div className="text-gray-400 text-sm mb-2" data-id="element-633">
                      {event.date}
                    </div>
                    <div className="text-gray-400 text-sm mb-3" data-id="element-634">
                      {event.location}
                    </div>
                    <div className="flex items-center text-gray-300 text-sm mb-3" data-id="element-635">
                      <UsersIcon size={14} className="inline mr-1" data-id="element-636" />
                      <span data-id="element-637">{event.interested} friends interested</span>
                    </div>
                    <div className="flex space-x-2" data-id="element-638">
                      <button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:from-sky-600 hover:to-blue-700 transition-colors flex-1" data-id="element-639">
                        View Details
                      </button>
                      <button className="border border-[#007BFF] text-[#007BFF] px-3 py-1.5 rounded-lg text-sm hover:bg-[#1A1A1D] transition-colors" data-id="element-640">
                        Share
                      </button>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>}
        {circleActiveTab === 'plans' && <div data-id="element-641">
            <div className="mb-4 flex justify-between items-center" data-id="element-642">
              <h2 className="text-xl font-semibold text-[#F5F5F7]" data-id="element-643">
                Group Plans
              </h2>
              <button className="bg-[#007BFF] text-white px-3 py-1.5 rounded-lg text-sm hover:bg-[#0069D9] transition-colors flex items-center" data-id="element-644">
                <PlusIcon size={16} className="mr-1" data-id="element-645" />
                New Plan
              </button>
            </div>
            <div className="space-y-4 mb-6" data-id="element-646">
              {plans.map(plan => <div key={plan.id} className="bg-[#2C2F33] rounded-xl p-4" data-id="element-647">
                  <div className="flex justify-between items-start mb-3" data-id="element-648">
                    <h3 className="text-lg font-bold text-[#F5F5F7]" data-id="element-649">
                      {plan.title}
                    </h3>
                    <span className="bg-[#1A1A1D] text-gray-300 text-xs px-2 py-1 rounded-full" data-id="element-650">
                      {plan.creator}
                    </span>
                  </div>
                  <div className="text-gray-400 text-sm mb-1" data-id="element-651">
                    <CalendarIcon size={14} className="inline mr-1" data-id="element-652" />
                    {plan.date}
                  </div>
                  <div className="text-gray-400 text-sm mb-3" data-id="element-653">
                    <svg xmlns="http://www.w3.org/2000/svg" className="inline h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-654">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" data-id="element-655" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" data-id="element-656" />
                    </svg>
                    {plan.location}
                  </div>
                  <div className="bg-[#1A1A1D] rounded-lg p-3 mb-3" data-id="element-657">
                    <div className="flex justify-between items-center mb-2" data-id="element-658">
                      <span className="text-gray-300 text-sm" data-id="element-659">Who's in?</span>
                      <span className="text-gray-400 text-xs" data-id="element-660">
                        {plan.responses.length} of{' '}
                        {selectedCircle.members.length} responded
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-661">
                      {plan.responses.map((response, index) => <div key={index} className="flex justify-between items-center" data-id="element-662">
                          <span className="text-[#F5F5F7] text-sm" data-id="element-663">
                            {response.name}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${response.status === 'going' ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'}`} data-id="element-664">
                            {response.status}
                          </span>
                        </div>)}
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-665">
                    <button className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-green-700 transition-colors flex-1" data-id="element-666">
                      I'm In
                    </button>
                    <button className="bg-yellow-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-yellow-700 transition-colors flex-1" data-id="element-667">
                      Maybe
                    </button>
                    <button className="bg-gray-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-gray-700 transition-colors flex-1" data-id="element-668">
                      Can't Go
                    </button>
                  </div>
                </div>)}
            </div>
            <div className="bg-[#2C2F33] rounded-xl p-4" data-id="element-669">
              <div className="flex items-center mb-4" data-id="element-670">
                <MessageCircleIcon size={18} className="text-[#007BFF] mr-2" data-id="element-671" />
                <h3 className="text-lg font-bold text-[#F5F5F7]" data-id="element-672">Group Chat</h3>
              </div>
              <div className="bg-[#1A1A1D] rounded-lg p-4 mb-4 h-40 flex items-center justify-center" data-id="element-673">
                <p className="text-gray-400 text-sm text-center" data-id="element-674">
                  Chat with your circle about upcoming plans
                  <br data-id="element-675" />
                  or anything else you want to discuss.
                </p>
              </div>
              <div className="flex" data-id="element-676">
                <input type="text" placeholder="Type a message..." className="flex-1 bg-[#1A1A1D] text-[#F5F5F7] rounded-l-lg px-4 py-2 border border-gray-700 focus:outline-none" data-id="element-677" />
                <button className="bg-[#007BFF] text-white px-4 py-2 rounded-r-lg hover:bg-[#0069D9] transition-colors" data-id="element-678">
                  Send
                </button>
              </div>
            </div>
          </div>}
        {circleActiveTab === 'interests' && <div data-id="element-679">
            <div className="mb-4 flex justify-between items-center" data-id="element-680">
              <h2 className="text-xl font-semibold text-[#F5F5F7]" data-id="element-681">
                Shared Interests
              </h2>
              <button className="text-[#007BFF] hover:underline text-sm flex items-center" data-id="element-682">
                <PlusIcon size={16} className="mr-1" data-id="element-683" />
                Add Interests
              </button>
            </div>
            <div className="bg-[#2C2F33] rounded-xl p-4 mb-6" data-id="element-684">
              <p className="text-gray-300 mb-4" data-id="element-685">
                These interests determine what events and content appear in this
                circle's feed.
              </p>
              <div className="space-y-3" data-id="element-686">
                {selectedCircle.interests.map(interest => <div key={interest} className="flex justify-between items-center bg-[#1A1A1D] rounded-lg p-3" data-id="element-687">
                    <div className="flex items-center" data-id="element-688">
                      <TagIcon size={16} className="text-[#007BFF] mr-2" data-id="element-689" />
                      <span className="text-[#F5F5F7]" data-id="element-690">{interest}</span>
                    </div>
                    <button className="text-gray-400 hover:text-[#F5F5F7]" data-id="element-691">
                      <MoreHorizontalIcon size={18} data-id="element-692" />
                    </button>
                  </div>)}
              </div>
              <div className="mt-4 border-t border-gray-700 pt-4" data-id="element-693">
                <h3 className="text-[#F5F5F7] font-medium mb-2" data-id="element-694">
                  Suggested Interests
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-695">
                  <button className="bg-[#1A1A1D] text-gray-300 text-sm px-3 py-1.5 rounded-full hover:bg-[#2C2F33] transition-colors" data-id="element-696">
                    <PlusIcon size={14} className="inline mr-1" data-id="element-697" />
                    Warriors
                  </button>
                  <button className="bg-[#1A1A1D] text-gray-300 text-sm px-3 py-1.5 rounded-full hover:bg-[#2C2F33] transition-colors" data-id="element-698">
                    <PlusIcon size={14} className="inline mr-1" data-id="element-699" />
                    Playoffs
                  </button>
                  <button className="bg-[#1A1A1D] text-gray-300 text-sm px-3 py-1.5 rounded-full hover:bg-[#2C2F33] transition-colors" data-id="element-700">
                    <PlusIcon size={14} className="inline mr-1" data-id="element-701" />
                    Sports Bars
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  };
  // Invite Friend Modal
  const renderInviteModal = () => {
    if (!showInviteModal) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-702">
        <div className="bg-[#22252A] rounded-xl max-w-md w-full shadow-lg border border-gray-700" data-id="element-703">
          <div className="p-6" data-id="element-704">
            <h2 className="text-xl font-bold text-[#F5F5F7] mb-4" data-id="element-705">
              Invite Friends to LaVee.life
            </h2>
            <p className="text-gray-300 mb-4" data-id="element-706">
              Send an invitation to connect and plan events together.
            </p>
            <div className="mb-4" data-id="element-707">
              <label className="block text-gray-300 text-sm mb-2" data-id="element-708">
                Email or Phone Number
              </label>
              <input type="text" placeholder="friend@example.com" className="w-full bg-[#2C2F33] text-[#F5F5F7] rounded-lg px-4 py-2 border border-gray-700 focus:border-[#007BFF] focus:outline-none" data-id="element-709" />
            </div>
            <div className="mb-4" data-id="element-710">
              <label className="block text-gray-300 text-sm mb-2" data-id="element-711">
                Add a Personal Message (Optional)
              </label>
              <textarea placeholder="Let's connect on LaVee.life to plan events together!" className="w-full bg-[#2C2F33] text-[#F5F5F7] rounded-lg px-4 py-2 border border-gray-700 focus:border-[#007BFF] focus:outline-none h-20 resize-none" data-id="element-712"></textarea>
            </div>
            <div className="flex space-x-3" data-id="element-713">
              <button onClick={() => setShowInviteModal(false)} className="flex-1 border border-gray-600 text-gray-300 px-4 py-2 rounded-lg hover:bg-[#2C2F33] transition-colors" data-id="element-714">
                Cancel
              </button>
              <button className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors" data-id="element-715">
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  // Create Circle Modal
  const renderCreateCircleModal = () => {
    if (!showCreateCircleModal) return null;
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-716">
        <div className="bg-[#22252A] rounded-xl max-w-md w-full shadow-lg border border-gray-700" data-id="element-717">
          <div className="p-6" data-id="element-718">
            <h2 className="text-xl font-bold text-[#F5F5F7] mb-4" data-id="element-719">
              Create New Circle
            </h2>
            <p className="text-gray-300 mb-4" data-id="element-720">
              Group friends with shared interests for easier planning.
            </p>
            <div className="mb-4" data-id="element-721">
              <label className="block text-gray-300 text-sm mb-2" data-id="element-722">
                Circle Name
              </label>
              <input type="text" placeholder="e.g., Game Day Crew" className="w-full bg-[#2C2F33] text-[#F5F5F7] rounded-lg px-4 py-2 border border-gray-700 focus:border-[#007BFF] focus:outline-none" data-id="element-723" />
            </div>
            <div className="mb-4" data-id="element-724">
              <label className="block text-gray-300 text-sm mb-2" data-id="element-725">
                Add Friends
              </label>
              <div className="max-h-40 overflow-y-auto bg-[#2C2F33] rounded-lg border border-gray-700 mb-2" data-id="element-726">
                {friends.map(friend => <div key={friend.id} className="flex items-center p-2 hover:bg-[#1A1A1D]" data-id="element-727">
                    <input type="checkbox" id={`friend-${friend.id}`} className="mr-3" data-id="element-728" />
                    <div className="w-8 h-8 rounded-full overflow-hidden mr-2" data-id="element-729">
                      <img src={friend.avatar} alt={friend.name} className="w-full h-full object-cover" data-id="element-730" />
                    </div>
                    <label htmlFor={`friend-${friend.id}`} className="text-[#F5F5F7] cursor-pointer" data-id="element-731">
                      {friend.name}
                    </label>
                  </div>)}
              </div>
              <button className="text-[#007BFF] text-sm hover:underline flex items-center" data-id="element-732">
                <UserPlusIcon size={14} className="mr-1" data-id="element-733" />
                Invite New Friend
              </button>
            </div>
            <div className="mb-6" data-id="element-734">
              <label className="block text-gray-300 text-sm mb-2" data-id="element-735">
                Circle Interests
              </label>
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-736">
                <span className="bg-[#007BFF] bg-opacity-20 text-[#007BFF] px-2 py-1 rounded-full text-sm flex items-center" data-id="element-737">
                  NBA
                  <button className="ml-1" data-id="element-738">×</button>
                </span>
                <span className="bg-[#007BFF] bg-opacity-20 text-[#007BFF] px-2 py-1 rounded-full text-sm flex items-center" data-id="element-739">
                  Lakers
                  <button className="ml-1" data-id="element-740">×</button>
                </span>
              </div>
              <div className="relative" data-id="element-741">
                <input type="text" placeholder="Add interests (e.g., NBA, concerts, food)" className="w-full bg-[#2C2F33] text-[#F5F5F7] rounded-lg px-4 py-2 border border-gray-700 focus:border-[#007BFF] focus:outline-none" data-id="element-742" />
              </div>
              <p className="text-gray-400 text-xs mt-1" data-id="element-743">
                These interests will help personalize the event feed for this
                circle
              </p>
            </div>
            <div className="flex space-x-3" data-id="element-744">
              <button onClick={() => setShowCreateCircleModal(false)} className="flex-1 border border-gray-600 text-gray-300 px-4 py-2 rounded-lg hover:bg-[#2C2F33] transition-colors" data-id="element-745">
                Cancel
              </button>
              <button className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-colors" data-id="element-746">
                Create Circle
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="w-full min-h-screen bg-[#1A1A1D] flex overflow-hidden" data-id="element-747">
      <div className="flex-1 flex flex-col h-screen overflow-hidden" data-id="element-748">
        <div className="flex-1 overflow-y-auto bg-[#1A1A1D]" data-id="element-749">
          <div className="max-w-7xl mx-auto" data-id="element-750">
            {selectedCircle ? renderCircleHub() : renderMainHub()}
            {renderInviteModal()}
            {renderCreateCircleModal()}
          </div>
        </div>
      </div>
    </div>;
};